import * as React from "react";
import { Button, cn, StateView, Eyebrow, useIsPhone } from "@ozpanel/ui";

// DataList — the shared table every list screen renders through. Columns are hand-configured
// per screen (D7's hybrid approach; NOT schema-driven yet — G7 is still open).
//
// Now carries what every list needs and none of them had: search, sortable columns, a
// per-row action slot, and the shared loading/error/empty renderer. A list with three rows
// works without any of that; a list with three hundred is unusable without all of it, and
// every one of these lists is backed by an endpoint that can return hundreds.

export interface DataListColumn<T> {
  key: string;
  label: string;
  render?: (row: T) => React.ReactNode;
  className?: string;
  /** Value used for sorting and free-text search. Falls back to the raw field at `key`.
   *  Supply this whenever `render` returns a node rather than a plain value — otherwise the
   *  column sorts/searches on something the operator cannot see. */
  value?: (row: T) => string | number | null | undefined;
  sortable?: boolean;
  /**
   * How the column shows on a phone, where the table becomes a stack of cards (below 640px):
   *  - "title": the card's heading (default: the first column)
   *  - "hide":  left off the card — for what is on the row only for scanning a long list
   *  - "sub":   in a compact list, the small line under the title (joined with " · ")
   *  - otherwise a "label  value" line on the card; in a compact list, not shown.
   */
  phone?: "title" | "hide" | "sub";
}

export interface DataListProps<T> {
  items: T[];
  columns: DataListColumn<T>[];
  getRowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  /**
   * Detail shown DIRECTLY BENEATH the row it belongs to, spanning the table.
   *
   * The alternative this replaces was a panel rendered after the whole table: opening the logs for
   * one app scrolled the reader to the bottom of the page, away from the row they clicked, with
   * nothing to say which row it belonged to. On a list of one that is merely odd; on a list of ten
   * it is unusable.
   *
   * Which row is open is owned by the CALLER, because opening a detail usually means fetching
   * something, and the fetch belongs to the screen rather than to a table.
   */
  renderExpanded?: (row: T) => React.ReactNode;
  /** The key of the currently expanded row, or null. */
  expandedKey?: string | null;
  /** Rendered in a trailing, right-aligned cell — the per-row action menu. */
  rowActions?: (row: T) => React.ReactNode;
  emptyMessage?: string;
  emptyHint?: string;
  /**
   * How rows show on a phone. "cards" (the default): a card per row, every column a labelled line.
   * "compact": one line per row — the title, a small line of the "sub" columns, the actions at the
   * right. For long lists of like rows: 110 DNS records as cards were 20,000px tall, 56 files 8,900px
   * and 32 sessions most of the Security page (measured 2026-11-02).
   */
  phoneDensity?: "cards" | "compact";
  /** Show this many rows, then "Show more" — for lists that can run to hundreds. Off by default. */
  pageSize?: number;
  /** Show the search box. Off for short, fixed lists (a node's 4 health checks). */
  searchable?: boolean;
  searchPlaceholder?: string;
  loading?: boolean;
  error?: unknown;
  errorContext?: string;
}

function cellValue<T>(col: DataListColumn<T>, row: T): string | number | null | undefined {
  if (col.value) return col.value(row);
  const raw = (row as Record<string, unknown>)[col.key];
  if (raw === null || raw === undefined) return raw as null | undefined;
  if (typeof raw === "string" || typeof raw === "number") return raw;
  return String(raw);
}

export function DataList<T>({
  items,
  columns,
  getRowKey,
  onRowClick,
  renderExpanded,
  expandedKey,
  rowActions,
  emptyMessage = "Nothing here yet.",
  emptyHint,
  phoneDensity = "cards",
  pageSize,
  searchable = false,
  searchPlaceholder = "Search…",
  loading,
  error,
  errorContext,
}: DataListProps<T>) {
  const [query, setQuery] = React.useState("");
  const [sort, setSort] = React.useState<{ key: string; dir: "asc" | "desc" } | null>(null);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((row) =>
      columns.some((col) => {
        const v = cellValue(col, row);
        return v !== null && v !== undefined && String(v).toLowerCase().includes(q);
      }),
    );
  }, [items, columns, query]);

  const sorted = React.useMemo(() => {
    if (!sort) return filtered;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return filtered;
    // Not sorted in place: `items` belongs to react-query's cache, and mutating it there
    // corrupts every other reader of the same query.
    return [...filtered].sort((a, b) => {
      const av = cellValue(col, a);
      const bv = cellValue(col, b);
      if (av === bv) return 0;
      if (av === null || av === undefined) return 1; // absent sorts last, either direction
      if (bv === null || bv === undefined) return -1;
      const cmp = typeof av === "number" && typeof bv === "number" ? av - bv : String(av).localeCompare(String(bv));
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [filtered, columns, sort]);

  const isPhone = useIsPhone();
  const sortId = React.useId();
  // Paging: the first pageSize rows, more on request. A new search starts from the top again.
  const [limit, setLimit] = React.useState(pageSize ?? 0);
  React.useEffect(() => setLimit(pageSize ?? 0), [pageSize, query]);
  const shown = pageSize && limit > 0 && sorted.length > limit ? sorted.slice(0, limit) : sorted;

  const toggleSort = (key: string) =>
    setSort((prev) =>
      prev?.key === key ? (prev.dir === "asc" ? { key, dir: "desc" } : null) : { key, dir: "asc" },
    );

  const table = (
    // min-w forces the table to keep its shape and the wrapper to scroll, rather than columns
    // crushing into unreadable slivers on a phone. Which of the two happens is decided here, and
    // the wrapper below is what makes the scroll reachable.
    <table className="w-full min-w-[560px] border-collapse text-left text-[13.5px]">
      <thead>
        <tr className="border-b border-line text-xs uppercase tracking-wide text-muted ">
          {columns.map((col) => (
            <th key={col.key} className={cn("sticky top-0 z-10 border-b border-line bg-surface2 px-4 py-2.5 font-normal", col.className)}>
              {col.sortable ? (
                <button
                  type="button" onClick={() => toggleSort(col.key)}
                  className="inline-flex items-center gap-1 uppercase hover:text-fg " >
                  {col.label}
                  <span aria-hidden className="text-[10px]">
                    {sort?.key === col.key ? (sort.dir === "asc" ? "▲" : "▼") : "↕"}
                  </span>
                </button>
              ) : (
                <Eyebrow>{col.label}</Eyebrow>
              )}
            </th>
          ))}
          {rowActions && <th className="sticky top-0 z-10 w-10 border-b border-line bg-surface2 px-4 py-2.5" />}
        </tr>
      </thead>
      <tbody>
        {shown.map((row) => {
          const key = getRowKey(row);
          const open = renderExpanded != null && expandedKey != null && expandedKey === key;
          return (
            <React.Fragment key={key}>
              <tr
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cn("border-b border-line",
                  onRowClick && "cursor-pointer hover:bg-surface2",
                  // An open row keeps its highlight, so the panel below it is unambiguously its
                  // own even when several columns of numbers look alike.
                  open && "bg-surface2",
                )}
              >
                {columns.map((col) => (
                  <td key={col.key} className={cn("px-4 py-2.5 tabular", col.className)}>
                    {col.render ? col.render(row) : String(cellValue(col, row) ?? "")}
                  </td>
                ))}
                {/* Never wrapped: wrapping actions made every Websites row ~64px tall on a desk. */}
                {rowActions && (
                  <td className="whitespace-nowrap px-4 py-2.5 text-right">
                    <div className="inline-flex flex-nowrap items-center justify-end gap-2 [&>*]:flex-nowrap">{rowActions(row)}</div>
                  </td>
                )}
              </tr>
              {open && (
                <tr className="border-b border-line bg-surface2">
                  <td colSpan={columns.length + (rowActions ? 1 : 0)} className="px-4 pb-4 pt-1">
                    {renderExpanded(row)}
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );

  // A phone gets CARDS, not a table that scrolls sideways. The table forced min-w-[560px], so on a
  // 320px phone a row's actions sat ~750px to the right behind a gesture nothing advertised, and a
  // long value (a browser's user-agent) was cut off mid-word (measured 2026-11-02). As a card every
  // value wraps, every action is in view, and an expanded row's detail opens under its own card.
  const titleCols = columns.filter((c) => c.phone === "title");
  const titleCol = titleCols[0] ?? columns[0];
  const sortable = columns.filter((c) => c.sortable);
  const cards = (
    <ul className="space-y-2">
      {shown.map((row) => {
        const key = getRowKey(row);
        const open = renderExpanded != null && expandedKey != null && expandedKey === key;
        const render = (col: DataListColumn<T>) => (col.render ? col.render(row) : String(cellValue(col, row) ?? ""));
        return (
          <li key={key} className={cn("rounded-card border border-line bg-surface", open && "border-[var(--ozp-accent-line)]")}>
            <div
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={cn("px-3.5 py-3", onRowClick && "cursor-pointer")}
            >
              {titleCol && <div className="min-w-0 text-[14px] font-medium break-words text-fg">{render(titleCol)}</div>}
              <dl className="mt-1.5 space-y-1">
                {columns
                  .filter((c) => c !== titleCol && c.phone !== "hide" && c.phone !== "title")
                  .map((col) => (
                    <div key={col.key} className="flex items-baseline justify-between gap-3 text-[13px]">
                      <dt className="shrink-0 text-[12px] text-muted">{col.label}</dt>
                      <dd className="min-w-0 text-right break-words tabular">{render(col)}</dd>
                    </div>
                  ))}
              </dl>
              {rowActions && (
                <div className="mt-2.5 flex flex-wrap justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                  {rowActions(row)}
                </div>
              )}
            </div>
            {open && <div className="border-t border-line bg-surface2 px-3.5 py-3">{renderExpanded(row)}</div>}
          </li>
        );
      })}
    </ul>
  );

  const subCols = columns.filter((c) => c.phone === "sub");
  const compact = (
    <ul className="divide-y divide-line rounded-card border border-line bg-surface">
      {shown.map((row) => {
        const key = getRowKey(row);
        const open = renderExpanded != null && expandedKey != null && expandedKey === key;
        const render = (col: DataListColumn<T>) => (col.render ? col.render(row) : String(cellValue(col, row) ?? ""));
        return (
          <li key={key} className={cn(open && "bg-surface2")}>
            <div
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={cn("flex min-h-12 items-center gap-2 px-3 py-2", onRowClick && "cursor-pointer")}
            >
              <div className="min-w-0 flex-1">
                {titleCol && <div className="truncate text-[13.5px] text-fg">{render(titleCol)}</div>}
                {subCols.length > 0 && (
                  <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 text-[11.5px] text-muted">
                    {subCols.map((col, i) => (
                      <React.Fragment key={col.key}>
                        {i > 0 && <span aria-hidden>·</span>}
                        <span className="min-w-0 break-words">{render(col)}</span>
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>
              {rowActions && (
                <div className="flex shrink-0 items-center gap-1" onClick={(e) => e.stopPropagation()}>
                  {rowActions(row)}
                </div>
              )}
            </div>
            {open && <div className="border-t border-line bg-surface2 px-3 py-3">{renderExpanded(row)}</div>}
          </li>
        );
      })}
    </ul>
  );

  return (
    <div>
      {isPhone && sortable.length > 0 && !loading && !error && items.length > 5 && (
        <div className="mb-2 flex items-center justify-end gap-2 text-[12.5px] text-muted">
          <label htmlFor={sortId}>Sort</label>
          <select
            id={sortId}
            value={sort ? `${sort.key}:${sort.dir}` : ""}
            onChange={(e) => {
              const [k, d] = e.target.value.split(":");
              setSort(k ? { key: k, dir: d === "desc" ? "desc" : "asc" } : null);
            }}
            className="rounded-lg border border-line bg-bg2 px-2 py-1.5 text-fg pointer-coarse:min-h-11"
          >
            <option value="">as listed</option>
            {sortable.flatMap((c) => [
              <option key={c.key + "a"} value={`${c.key}:asc`}>{c.label} ↑</option>,
              <option key={c.key + "d"} value={`${c.key}:desc`}>{c.label} ↓</option>,
            ])}
          </select>
        </div>
      )}
      {searchable && !loading && !error && items.length > 0 && (
        <div className="mb-3 flex items-center gap-3">
          <input
            type="search" value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full rounded-lg border border-line bg-bg2 sm:w-64 px-3 py-[7px] text-[13px] text-fg outline-none placeholder:text-faint focus:border-accent" />
          {query && (
            <span className="font-mono text-[11px] text-faint">
              {sorted.length} of {items.length}
            </span>
          )}
        </div>
      )}
      <StateView
        loading={loading}
        error={error}
        errorContext={errorContext}
        empty={!loading && !error && sorted.length === 0}
        // An empty RESULT and an empty SOURCE are different facts and must not read the same:
        // "no backup targets" is a thing to fix, "no match for 'xyz'" is a typo.
        emptyMessage={items.length > 0 && query ? `Nothing matches “${query}”.` : emptyMessage}
        emptyHint={items.length > 0 && query ? undefined : emptyHint}
        emptyCompact
      >
        {/* overflow-x-auto, not overflow-hidden. Hidden CLIPPED the right-hand columns on any
            narrow screen — actions, status, size — with no way to reach them, so a phone showed a
            table that looked complete and was missing the half that lets you act on a row. */}
        {isPhone ? (phoneDensity === "compact" ? compact : cards) : <div className="overflow-x-auto rounded-card border border-line bg-surface">{table}</div>}
        {shown.length < sorted.length && (
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[12.5px] text-muted">
            <span>
              Showing {shown.length} of {sorted.length}
            </span>
            <span className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => setLimit((l) => l + (pageSize ?? 50))}>
                Show {Math.min(pageSize ?? 50, sorted.length - shown.length)} more
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setLimit(sorted.length)}>
                Show all
              </Button>
            </span>
          </div>
        )}
      </StateView>
    </div>
  );
}
